import type { ErrorEvent } from '@sentry/browser';
import type { IOrderInfo } from '../types/order.js';

type SentryEvent = Pick<ErrorEvent, 'exception'>;

export const CHECKOUT_SENTRY_TAGS = {
  sdk: 'checkout.sdk',
  merchantClientId: 'merchant.client_id',
  merchantAccountId: 'merchant.account_id',
  orderId: 'order.id',
  merchantTransactionId: 'order.merchant_transaction_id',
} as const;

type CheckoutSentryScope = {
  setTags(tags: Record<string, string | undefined>): void;
  setContext(name: string, context: Record<string, string> | null): void;
};

const CHECKOUT_SENTRY_SOURCE_PATHS = ['/acquirer/checkout/'];

function value(value?: string): string | undefined {
  try {
    const normalized = value?.trim();
    return normalized ? normalized.slice(0, 200) : undefined;
  } catch {
    return value;
  }
}

/** 外部渠道 SDK 默认不受信任；只有本仓库实际发布路径才可加入白名单。 */
export function shouldSendCheckoutSentryEvent(event: SentryEvent): boolean {
  try {
    const exceptions = event.exception?.values;
    if (!exceptions?.length) return false;
    return exceptions.every(exception => {
      const filename =
        exception.stacktrace?.frames?.[exception.stacktrace.frames.length - 1]?.filename;
      if (!filename) return false;
      return CHECKOUT_SENTRY_SOURCE_PATHS.some(path => filename.includes(path));
    });
  } catch {
    return true; // 兜底：若 Sentry SDK 解析异常，则默认发送事件
  }
}

export function syncCheckoutSentryContext(
  sentry: CheckoutSentryScope,
  orderInfo: Partial<IOrderInfo>,
  instanceId: string
): void {
  try {
    const tags: Array<[string, string | undefined]> = [
      [CHECKOUT_SENTRY_TAGS.sdk, 'acquirer-checkout-front'],
      [CHECKOUT_SENTRY_TAGS.merchantClientId, value(orderInfo.clientId)],
      [CHECKOUT_SENTRY_TAGS.merchantAccountId, value(orderInfo.accId)],
      [CHECKOUT_SENTRY_TAGS.orderId, value(orderInfo.orderId)],
      [CHECKOUT_SENTRY_TAGS.merchantTransactionId, value(orderInfo.merchantTransactionId)],
    ];
    const context: Array<[string, string | undefined]> = [
      ['instance_id', value(instanceId)],
      ['country', value(orderInfo.country)],
      ['language', value(orderInfo.language)],
    ];
    sentry.setTags(Object.fromEntries(tags.filter(([, item]) => item)) as Record<string, string>);
    sentry.setContext(
      'checkout',
      Object.fromEntries(context.filter(([, item]) => item)) as Record<string, string>
    );
  } catch {
    // ignore
  }
}

export function clearCheckoutSentryContext(sentry: CheckoutSentryScope): void {
  try {
    sentry.setTags({
      [CHECKOUT_SENTRY_TAGS.sdk]: undefined,
      [CHECKOUT_SENTRY_TAGS.merchantClientId]: undefined,
      [CHECKOUT_SENTRY_TAGS.merchantAccountId]: undefined,
      [CHECKOUT_SENTRY_TAGS.orderId]: undefined,
      [CHECKOUT_SENTRY_TAGS.merchantTransactionId]: undefined,
    });
    sentry.setContext('checkout', null);
  } catch {
    // ignore
  }
}
